import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { StyledDialog } from '../StyledDialog';
import { buttonLabels, dialogMessages } from '@/constants';
import { Text } from '@/components/primitives';
import type { DialogSize } from '@/components/templates';

interface ConfirmDialogBaseProps {
  open: boolean;
  onClose: () => void;
  /**
   * The action to run when the user confirms.
   *
   * Return a promise (e.g. `() => mutateAsync(...)`) to hand the dialog its
   * own lifecycle: it drives the loading state and closes itself once the
   * promise settles — see `keepOpenOnError` for the one case where it
   * shouldn't. Return anything else (including nothing) to keep managing
   * `loading`/`onClose` yourself; non-promise return values are ignored.
   *
   * Typed as `unknown` rather than `void | Promise<unknown>` on purpose: a
   * union return type loses TypeScript's special case for void-returning
   * signatures, which would reject the common
   * `() => item && mutate(item.id)` short-circuit.
   */
  onConfirm: () => unknown;
  /** Ignored by `appearance="compact"`: the message is the accessible name there. */
  title?: string;
  confirmText?: string;
  cancelText?: string;
  /** @deprecated Use variant='destructive' instead */
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';
  /** Use 'destructive' for delete/remove actions */
  variant?: 'confirm' | 'destructive';
  loading?: boolean;
  /**
   * Dialog width. Leave unset for the default: 'standard' (600px) on its own,
   * and 'compact' (444px) when stacked over another dialog, so a nested
   * confirm reads as a subordinate prompt instead of a same-size replacement.
   * Setting this pins the width in both cases.
   */
  size?: DialogSize;
  /**
   * Keep the dialog open when `onConfirm` rejects.
   *
   * Off by default, because a confirm is a *decision* surface: once the user
   * has answered, it has done its job, and leaving it up asks them to re-answer
   * a question they already answered — with no affordance to fix anything. The
   * outcome belongs to the surface that owns the work, which is where
   * `useFeedbackZone` now routes it.
   *
   * Turn this on only when the error is correctable *inside the confirm* — a
   * mistyped "type DELETE to confirm", say — so there's actually something for
   * the user to do here.
   */
  keepOpenOnError?: boolean;
  /** Forwarded to StyledDialog `testId` (a data-testid on the dialog content root). */
  testId?: string;
}

/**
 * `appearance`: 'standard' (default, every existing caller) or 'compact' (Q-530):
 * a content-sized one-sentence prompt with no X and no visible title. Compact
 * takes `message: string` only, because the message is the dialog's accessible name.
 */
export type ConfirmDialogProps = ConfirmDialogBaseProps &
  (
    | { appearance?: 'standard'; message: string | React.ReactNode }
    | { appearance: 'compact'; message: string }
  );

function isPromise(value: unknown): value is Promise<unknown> {
  return typeof (value as Promise<unknown> | undefined)?.then === 'function';
}

/**
 * Confirm Dialog - confirmation with Cancel/Confirm buttons
 * Uses StyledDialog with clean appearance (minimal chrome, content-focused)
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = dialogMessages.confirm.title,
  message,
  confirmText = buttonLabels.primary.yes,
  cancelText = buttonLabels.secondary.cancel,
  confirmColor,
  variant,
  loading = false,
  size,
  keepOpenOnError = false,
  appearance = 'standard',
  testId,
}: ConfirmDialogProps) {
  // Map legacy confirmColor to variant (confirmColor takes precedence if both provided)
  const effectiveVariant =
    confirmColor === 'error' ? 'destructive' : variant ?? 'confirm';

  // Set only while awaiting a promise-returning onConfirm. Callers that manage
  // their own lifecycle keep driving `loading` and never touch this.
  const [settling, setSettling] = useState(false);

  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleConfirm = useCallback(() => {
    const result = onConfirm();
    if (!isPromise(result)) return;

    setSettling(true);
    const finish = (shouldClose: boolean) => {
      if (isMounted.current) setSettling(false);
      if (shouldClose) onClose();
    };
    // Settled means answered — close on success *and* on failure. The rejection
    // is deliberately absorbed rather than rethrown: the mutation layer has
    // already reported it through `useFeedbackZone`, and rethrowing from here
    // would only surface as an unhandled rejection.
    result.then(
      () => finish(true),
      () => finish(!keepOpenOnError),
    );
  }, [onConfirm, onClose, keepOpenOnError]);

  const busy = loading || settling;

  // Compact: Radix returns focus only to a Dialog.Trigger and a controlled dialog has none, so
  // closing would drop focus to <body>. Record the opener when the dialog opens (a layout effect
  // runs before Radix moves focus into the dialog) and put focus back on it on close.
  const opener = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (open && appearance === 'compact') opener.current = document.activeElement as HTMLElement | null;
  }, [open, appearance]);
  const restoreOpenerFocus = useCallback((event: Event) => {
    const el = opener.current;
    opener.current = null;
    if (el && el.isConnected && el !== document.body) {
      event.preventDefault();
      el.focus();
    }
  }, []);

  const footer = {
    variant: effectiveVariant,
    primaryText: confirmText,
    secondaryText: cancelText,
    onPrimary: handleConfirm,
    onSecondary: onClose,
    loading: busy,
  } as const;

  if (appearance === 'compact') {
    return (
      <StyledDialog
        open={open}
        onClose={onClose}
        appearance="compact"
        title={message as string}
        hideTitle
        testId={testId}
        onCloseAutoFocus={restoreOpenerFocus}
        closeDisabled={busy}
        footer={footer}
      >
        <Text>{message}</Text>
      </StyledDialog>
    );
  }

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      size={size ?? 'standard'}
      nestedSize={size ?? 'compact'}
      title={title}
      testId={testId}
      closeDisabled={busy}
      footer={footer}
    >
      {typeof message === 'string' ? (
        <Text>{message}</Text>
      ) : (
        message
      )}
    </StyledDialog>
  );
}
